import { supabase } from '../lib/supabase';

export interface NotificationCounts {
  total: number;
  messages: number;
  quotes: number;
  appointments: number;
  jobs: number;
  contracts: number;
  wheelEntries: number;
  wheelWins: number;
}

/**
 * Ejecuta las consultas de conteo en tandas pequeñas.
 *
 * Motivo: la campanita lanzaba 7 peticiones simultáneas a Supabase y, sumadas a
 * las del dashboard, iban todas por la misma conexión HTTP/2. Si el edge cerraba
 * esa conexión, todas las consultas en vuelo fallaban juntas (ERR_CONNECTION_CLOSED).
 */
const COUNT_BATCH_SIZE = 3;

async function runCountBatches(queries: (() => Promise<number>)[]): Promise<number[]> {
  const counts: number[] = [];

  for (let i = 0; i < queries.length; i += COUNT_BATCH_SIZE) {
    const batch = queries.slice(i, i + COUNT_BATCH_SIZE);
    const batchCounts = await Promise.all(batch.map((run) => run()));
    counts.push(...batchCounts);
  }

  return counts;
}

/**
 * Marca todas las notificaciones pendientes como revisadas/atendidas
 */
export async function dismissAllNotifications(): Promise<void> {
  try {
    await Promise.all([
      // Leads nuevos → en revisión
      supabase
        .from('leads')
        .update({ status: 'in_review' })
        .eq('status', 'new'),

      // Leads leídos → en revisión
      supabase
        .from('leads')
        .update({ status: 'in_review' })
        .eq('status', 'read'),

      // Citas solicitadas → confirmadas
      supabase
        .from('appointments')
        .update({ status: 'confirmed' })
        .eq('status', 'requested'),

      // Trabajos completados → marcar como revisados por admin
      supabase
        .from('jobs')
        .update({ admin_reviewed_at: new Date().toISOString() })
        .eq('status', 'completed')
        .not('client_completed_at', 'is', null)
        .is('admin_reviewed_at', null),

      // Contratos firmados → marcar como revisados por admin
      supabase
        .from('contracts')
        .update({ admin_reviewed_at: new Date().toISOString() })
        .eq('status', 'signed')
        .is('admin_reviewed_at', null),

      // Cotizaciones nuevas → Contactada
      supabase
        .from('cotizaciones')
        .update({ estado: 'Contactada', updated_at: new Date().toISOString() })
        .eq('estado', 'Nueva'),

      // Ruleta entradas → marcar como revisadas
      supabase
        .from('ruleta_giros')
        .update({ is_reviewed: true })
        .eq('is_reviewed', false),

      // Ruleta premios → marcar como reclamados
      supabase
        .from('ruleta_giros')
        .update({ reclamado: true })
        .eq('reclamado', false)
        .not('premio_nombre', 'is', null),
    ]);
  } catch (error) {
    console.error('Error al descartar notificaciones:', error);
    throw error;
  }
}

export async function getNotificationCounts(): Promise<NotificationCounts> {
  const queries: (() => Promise<number>)[] = [
    // MENSAJES/LEADS - status = 'new'
    async () => {
      const { count, error } = await supabase
        .from('leads')
        .select('id', { count: 'exact' })
        .eq('status', 'new');
      if (error) throw new Error(error.message);
      return count ?? 0;
    },

    // COTIZACIONES - estado = 'Nueva' (recién llegadas del funnel)
    async () => {
      const { count, error } = await supabase
        .from('cotizaciones')
        .select('id', { count: 'exact' })
        .eq('estado', 'Nueva');
      if (error) throw new Error(error.message);
      return count ?? 0;
    },

    // CITAS - solo status = 'requested' (pendientes de confirmar)
    async () => {
      const { count, error } = await supabase
        .from('appointments')
        .select('id', { count: 'exact' })
        .eq('status', 'requested');
      if (error) throw new Error(error.message);
      return count ?? 0;
    },

    // TRABAJOS - completados por cliente, no revisados por admin
    async () => {
      const { count, error } = await supabase
        .from('jobs')
        .select('id', { count: 'exact' })
        .eq('status', 'completed')
        .not('client_completed_at', 'is', null)
        .is('admin_reviewed_at', null);
      if (error) throw new Error(error.message);
      return count ?? 0;
    },

    // RULETA - entradas no revisadas
    async () => {
      const { count, error } = await supabase
        .from('ruleta_giros')
        .select('id', { count: 'exact' })
        .eq('is_reviewed', false);
      if (error) throw new Error(error.message);
      return count ?? 0;
    },

    // RULETA - premios ganados no reclamados
    async () => {
      const { count, error } = await supabase
        .from('ruleta_giros')
        .select('id', { count: 'exact' })
        .eq('reclamado', false)
        .not('premio_nombre', 'is', null);
      if (error) throw new Error(error.message);
      return count ?? 0;
    },

    // CONTRATOS - firmados y no revisados por admin
    async () => {
      const { count, error } = await supabase
        .from('contracts')
        .select('id', { count: 'exact' })
        .eq('status', 'signed')
        .is('admin_reviewed_at', null);
      if (error) throw new Error(error.message);
      return count ?? 0;
    },
  ];

  // Si alguna consulta falla, el error se propaga: el llamador conserva el
  // último conteo conocido y muestra "sin conexión" en vez de ceros falsos.
  const counts = await runCountBatches(queries);
  const messages = counts[0];
  const quotes = counts[1];
  const appointments = counts[2];
  const jobs = counts[3];
  const wheelEntries = counts[4];
  const wheelWins = counts[5];
  const contracts = counts[6];

  const total = messages + quotes + appointments + jobs + contracts + wheelEntries + wheelWins;

  return {
    total,
    messages,
    quotes,
    appointments,
    jobs,
    contracts,
    wheelEntries,
    wheelWins,
  };
}
